import * as ngeohash from 'ngeohash';
import * as OLC from 'open-location-code';
import * as mgrs from 'mgrs';

export interface GeoPoint {
  lat: number;
  lon: number;
}

export const toRad = (deg: number): number => (deg * Math.PI) / 180;
export const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/** Normalize longitude into [-180, 180). */
export function normLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

export function clampLat(lat: number): number {
  return Math.min(90, Math.max(-90, lat));
}

export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

const COMPASS_POINTS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
];

export function compass(deg: number): string {
  const norm = ((deg % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(norm / 22.5) % 16];
}

/* ---------- Coordinate formats ---------- */

export function formatDD(p: GeoPoint): string {
  const latH = p.lat >= 0 ? 'N' : 'S';
  const lonH = p.lon >= 0 ? 'E' : 'W';
  return `${Math.abs(p.lat).toFixed(5)}° ${latH}, ${Math.abs(p.lon).toFixed(5)}° ${lonH}`;
}

function dmsParts(dec: number): [number, number, number] {
  const abs = Math.abs(dec);
  const d = Math.floor(abs);
  const mFloat = (abs - d) * 60;
  const m = Math.floor(mFloat);
  const s = (mFloat - m) * 60;
  return [d, m, s];
}

/** Degrees, minutes, seconds. */
export function formatDMS(p: GeoPoint): string {
  const [latD, latM, latS] = dmsParts(p.lat);
  const [lonD, lonM, lonS] = dmsParts(p.lon);
  const latH = p.lat >= 0 ? 'N' : 'S';
  const lonH = p.lon >= 0 ? 'E' : 'W';
  return `${latD}° ${latM}′ ${latS.toFixed(1)}″ ${latH}, ${lonD}° ${lonM}′ ${lonS.toFixed(1)}″ ${lonH}`;
}

/** Degrees and decimal minutes. */
export function formatDDM(p: GeoPoint): string {
  const latAbs = Math.abs(p.lat);
  const lonAbs = Math.abs(p.lon);
  const latH = p.lat >= 0 ? 'N' : 'S';
  const lonH = p.lon >= 0 ? 'E' : 'W';
  return `${Math.floor(latAbs)}° ${((latAbs % 1) * 60).toFixed(3)}′ ${latH}, ${Math.floor(lonAbs)}° ${((lonAbs % 1) * 60).toFixed(3)}′ ${lonH}`;
}

/** Geohash (9 chars ≈ 5 m precision). */
export function geohash(p: GeoPoint, precision = 9): string {
  return ngeohash.encode(p.lat, p.lon, precision);
}

/** Maidenhead locator (6-character ham radio grid square). */
export function maidenhead(p: GeoPoint): string {
  let lon = Math.min(179.999999, Math.max(-180, p.lon)) + 180;
  let lat = Math.min(89.999999, Math.max(-90, p.lat)) + 90;
  const upper = (n: number) => String.fromCharCode(65 + n);
  const lower = (n: number) => String.fromCharCode(97 + n);

  const fieldLon = Math.min(17, Math.floor(lon / 20));
  lon -= fieldLon * 20;
  const fieldLat = Math.min(17, Math.floor(lat / 10));
  lat -= fieldLat * 10;

  const squareLon = Math.floor(lon / 2);
  lon -= squareLon * 2;
  const squareLat = Math.floor(lat);
  lat -= squareLat;

  const subLon = Math.min(23, Math.floor(lon * 12)); // 2° / 24 = 5′
  const subLat = Math.min(23, Math.floor(lat * 24)); // 1° / 24 = 2.5′

  return `${upper(fieldLon)}${upper(fieldLat)}${squareLon}${squareLat}${lower(subLon)}${lower(subLat)}`;
}

interface OlcLike {
  encode(latitude: number, longitude: number, codeLength?: number): string;
}

function olcApi(): OlcLike | null {
  const mod = OLC as unknown as { OpenLocationCode?: OlcLike } & Partial<OlcLike>;
  if (mod.OpenLocationCode && typeof mod.OpenLocationCode.encode === 'function') {
    return mod.OpenLocationCode;
  }
  if (typeof mod.encode === 'function') return mod as OlcLike;
  return null;
}

/** Google Plus Code (Open Location Code), 10-digit global code. */
export function plusCode(p: GeoPoint): string {
  try {
    const api = olcApi();
    if (!api) return '—';
    return api.encode(p.lat, p.lon, 10);
  } catch {
    return '—';
  }
}

/** Military Grid Reference System (1 m precision). */
export function mgrsRef(p: GeoPoint): string {
  try {
    if (p.lat > 84 || p.lat < -80) return 'N/A (polar region)';
    return mgrs.forward([p.lon, p.lat], 5);
  } catch {
    return '—';
  }
}

/* ---------- UTM (WGS84) ---------- */

export interface UtmCoord {
  zone: number;
  band: string;
  easting: number;
  northing: number;
  hemisphere: 'N' | 'S';
}

const WGS84_A = 6378137;
const WGS84_F = 1 / 298.257223563;
const UTM_BANDS = 'CDEFGHJKLMNPQRSTUVWX';

export function utm(p: GeoPoint): UtmCoord | null {
  if (p.lat > 84 || p.lat < -80) return null;
  const e2 = WGS84_F * (2 - WGS84_F);
  const ep2 = e2 / (1 - e2);
  const zone = Math.floor((p.lon + 180) / 6) + 1;
  const lon0 = toRad((zone - 1) * 6 - 180 + 3);
  const lat = toRad(p.lat);
  const lon = toRad(p.lon);
  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const tanLat = Math.tan(lat);

  const N = WGS84_A / Math.sqrt(1 - e2 * sinLat * sinLat);
  const T = tanLat * tanLat;
  const C = ep2 * cosLat * cosLat;
  const A = cosLat * (lon - lon0);

  const M =
    WGS84_A *
    ((1 - e2 / 4 - (3 * e2 ** 2) / 64 - (5 * e2 ** 3) / 256) * lat -
      ((3 * e2) / 8 + (3 * e2 ** 2) / 32 + (45 * e2 ** 3) / 1024) * Math.sin(2 * lat) +
      ((15 * e2 ** 2) / 256 + (45 * e2 ** 3) / 1024) * Math.sin(4 * lat) -
      ((35 * e2 ** 3) / 3072) * Math.sin(6 * lat));

  const k0 = 0.9996;
  const easting =
    k0 * N * (A + ((1 - T + C) * A ** 3) / 6 + ((5 - 18 * T + T * T + 72 * C - 58 * ep2) * A ** 5) / 120) +
    500000;
  let northing =
    k0 *
    (M +
      N *
        tanLat *
        ((A * A) / 2 +
          ((5 - T + 9 * C + 4 * C * C) * A ** 4) / 24 +
          ((61 - 58 * T + T * T + 600 * C - 330 * ep2) * A ** 6) / 720));
  if (p.lat < 0) northing += 10000000;

  const band = UTM_BANDS[Math.min(19, Math.floor((p.lat + 80) / 8))];
  return {
    zone,
    band,
    easting: Math.round(easting),
    northing: Math.round(northing),
    hemisphere: p.lat >= 0 ? 'N' : 'S',
  };
}

export function formatUtm(p: GeoPoint): string {
  const u = utm(p);
  if (!u) return 'N/A (polar region)';
  return `${u.zone}${u.band} ${u.easting} mE ${u.northing} mN`;
}
